import * as React from 'react';
import { useSelector } from 'react-redux';
import { ModalWindow, UIHeading2 } from '@cian/ui-kit';

import {
  InfoPlate,
  InfrastructureTypeRows,
  NoInfrastructureOutside,
  OutsideIcon,
  RateInfo,
  TransportAccessibility,
} from '../../../../components/Map/parts/TransportAccessibility';
import { MODAL_Z_INDEX } from '../../../../constants/infrastructure';
import { MAX_TRANSPORT_ACCESSIBILITY_RATE } from '../../../../constants/transportAccessibility';
import {
  selectRegularInfrastructure,
  selectTransportAccessibilityInfrastructure,
} from '../../../../selectors/infrastructure';
import { getTransportAccessibility, getTransportAccessibilitySource } from '../../../../selectors/newbuilding';
import { ITransportAccessibility } from '../../../../types/newbuilding/transportAccessibility';
import { ETransportAccessibilityTab, TTAInfrastructureType } from '../../../../types/map';
import { prepareRate, prepareTransportAccessibility } from '../../../../utils/transportAccessibility';
import { getOutsideRows } from './helpers';
import { trackDepartmentLinkClick, trackInfrastructureClick, trackRateClick } from '../../analytics';

export interface ITransportAccessibilityOutsideProps {
  onTransportAccessibilityChange: (infrastructureType: TTAInfrastructureType | null) => void;
}

export const TransportAccessibilityOutside: React.FC<ITransportAccessibilityOutsideProps> = ({
  onTransportAccessibilityChange,
}) => {
  const { rate, rateDetails, title } = useSelector(getTransportAccessibility) as ITransportAccessibility;
  const departmentLink = useSelector(getTransportAccessibilitySource);

  const displayRate = prepareRate(rate);
  const displayMaxRate = String(MAX_TRANSPORT_ACCESSIBILITY_RATE);
  const displayTerms = React.useMemo(() => prepareTransportAccessibility(rateDetails), [rateDetails]);

  const transportAccesibilityInfrastructure = useSelector(selectTransportAccessibilityInfrastructure);
  const clickableRows = React.useMemo(
    () => getOutsideRows(transportAccesibilityInfrastructure),
    [transportAccesibilityInfrastructure],
  );

  const regularInfrastructure = useSelector(selectRegularInfrastructure);

  const [isModalOpen, setModal] = React.useState(false);

  const openModal = React.useCallback(() => {
    setModal(true);
    trackRateClick(ETransportAccessibilityTab.Outside);
  }, []);

  const closeModal = React.useCallback(() => setModal(false), []);

  const [selectedType, setSelectedType] = React.useState(clickableRows[0]?.id);

  React.useEffect(() => {
    onTransportAccessibilityChange(selectedType as TTAInfrastructureType);

    return () => onTransportAccessibilityChange(null);
  }, [onTransportAccessibilityChange]);

  const handleRowClick = React.useCallback(
    (infrastructureType: TTAInfrastructureType) => {
      onTransportAccessibilityChange(infrastructureType);
      setSelectedType(infrastructureType);

      trackInfrastructureClick({
        infrastructureType,
        infrastructureItems: regularInfrastructure,
      });
    },
    [regularInfrastructure],
  );

  const handleDepartmentLinkClick = React.useCallback(() => {
    trackDepartmentLinkClick(ETransportAccessibilityTab.Outside);
  }, []);

  return (
    <>
      <InfoPlate
        icon={<OutsideIcon />}
        content={<RateInfo rate={rate} />}
        data-testid="TransportAccessibilityOutsideInfo"
        withHorizontalMargin
        onClick={openModal}
      />
      {clickableRows.length > 0 ? (
        <InfrastructureTypeRows
          clickableRows={clickableRows}
          nonClickableRows={[]}
          onRowClick={handleRowClick}
          selectedItemId={selectedType}
        />
      ) : (
        <NoInfrastructureOutside />
      )}
      <ModalWindow
        content={
          <TransportAccessibility
            departmentLink={departmentLink}
            maxRate={displayMaxRate}
            onDepartmentLinkClick={handleDepartmentLinkClick}
            rate={displayRate}
            terms={displayTerms}
          />
        }
        header={<UIHeading2>{title}</UIHeading2>}
        escape
        width={759}
        onClose={closeModal}
        open={isModalOpen}
        size="M"
        zIndex={MODAL_Z_INDEX}
      />
    </>
  );
};
